import { useState } from "react";
import { Mail, Copy, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";

// Simple, calm contact page. Deliberately plain so the email is the focus.
const CONTACT_EMAIL = "apindex1@gmail.com";

const FAQS = [
  {
    q: "How does billing work?",
    a: "Pro Pass is $10/month or $99/year, billed through Stripe. New subscribers get a 7-day free trial — you're not charged until the trial ends, and you can cancel any time before that and pay nothing. Tips and Premium Themes are one-time charges, not subscriptions.",
  },
  {
    q: "How do I cancel my Pro Pass?",
    a: "Go to Account → Pro Pass → Cancel Pro Pass. Cancellation takes effect at the end of your current paid period, so you keep access until then. No forms, no phone calls, no retention pressure — one confirmation and you're done.",
  },
  {
    q: "Do you offer refunds?",
    a: "Because Pro Pass includes a full 7-day free trial and cancels at period end (so you always finish what you paid for), we generally don't issue partial-month refunds. If something specific went wrong on our side — a double charge, a failed feature — email us and we'll make it right.",
  },
  {
    q: "Is my catalogue private?",
    a: "Yes. Your anime list, ratings, and notes are visible only to you. Persona shares are opt-in per snapshot: nothing is public unless you explicitly hit \"Share\". You can export or delete everything from Account at any time — no support ticket required.",
  },
];

function FaqItem({ q, a }) {
  return (
    <details
      data-testid={`faq-item-${q.slice(0, 20).toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="group border-b border-white/10 last:border-b-0"
    >
      <summary
        className="flex cursor-pointer items-center justify-between gap-4 py-4 text-left font-medium text-white transition-colors hover:text-[#FF3B30] [&::-webkit-details-marker]:hidden"
      >
        <span className="text-base md:text-lg">{q}</span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-neutral-500 transition-transform duration-200 group-open:rotate-180 group-open:text-[#FF3B30]" />
      </summary>
      <p className="pb-5 pr-8 text-sm leading-relaxed text-neutral-300">{a}</p>
    </details>
  );
}

export default function SupportContact() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      toast.success("Email copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: focus + select so the user can copy manually
      toast.error("Couldn't copy — long-press the email to copy manually");
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-24 md:px-10" data-testid="support-page">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 11</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
        Support
      </h1>
      <p className="mt-6 text-base leading-relaxed text-neutral-300">
        Most questions are already answered below. If yours isn&apos;t, drop us a line — we usually reply within a day or two.
      </p>

      {/* FAQ — collapsible, native <details> so it's accessible without JS state */}
      <section data-testid="support-faq" className="mt-8 border border-white/10 bg-neutral-950 px-5">
        <p className="border-b border-white/10 py-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          Common questions
        </p>
        {FAQS.map((f) => (
          <FaqItem key={f.q} q={f.q} a={f.a} />
        ))}
      </section>

      <a
        href="/roadmap"
        data-testid="support-roadmap-link"
        className="mt-6 flex items-center justify-between border border-white/10 bg-neutral-950 p-4 hover:border-[#FF3B30]"
      >
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">Have an idea?</p>
          <p className="mt-1 text-sm text-white">See the public roadmap and upvote what you want next →</p>
        </div>
      </a>

      <div className="mt-10 flex items-center gap-3">
        <div className="h-px flex-1 bg-white/10" />
        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Still stuck?</p>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      <p className="mt-6 text-base leading-relaxed text-neutral-300">
        Contact us with any questions, concerns, or suggestions at{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          data-testid="support-email-link"
          className="text-[#FF3B30] underline decoration-[#FF3B30]/40 underline-offset-4 hover:decoration-[#FF3B30] break-all"
        >
          {CONTACT_EMAIL}
        </a>
        .
      </p>

      {/* Big tap/copy target — sized generously for mobile */}
      <div className="mt-10 flex flex-col gap-3 border border-white/15 bg-neutral-950 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Mail className="h-5 w-5 flex-shrink-0 text-[#FF3B30]" />
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="support-email-button"
            className="font-mono text-base text-white break-all hover:text-[#FF3B30] md:text-lg"
          >
            {CONTACT_EMAIL}
          </a>
        </div>
        <button
          data-testid="support-copy-btn"
          onClick={copyEmail}
          className="flex flex-shrink-0 items-center justify-center gap-2 border border-white/20 bg-white px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-black hover:bg-[#FF3B30] hover:text-white active:scale-95 transition-transform"
          aria-label="Copy email address"
        >
          {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy email</>}
        </button>
      </div>

      <p className="mt-8 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
        Handled by a human — not a chatbot.
      </p>
    </div>
  );
}
